import React, { ReactElement, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  MdOutlineApi,
  MdOutlineAssessment,
  MdOutlineCloud,
  MdOutlineDescription,
  MdOutlineEuro,
  MdOutlineSensors,
  MdOutlineSettingsSuggest,
  MdOutlineTrendingUp,
} from "react-icons/md";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { ProductOffering } from "./Products.types";
import { formatPrice } from "./helpers";

export const Products = (): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  usePageTitle(gettext("Products"));

  const serviceOfferings: ProductOffering[] = [
    {
      name: gettext("Energy"),
      price: 149,
      description: gettext(
        "Master your usage and your footprint. Track real-time energy consumption alongside critical climate data to optimize your efficiency and seamlessly hit your sustainability targets.",
      ),
      highlights: [
        gettext("Track Live Usage"),
        gettext("Optimize Footprint"),
        gettext("Boost Efficiency"),
      ],
    },
    {
      name: gettext("Climate"),
      price: 129,
      description: gettext(
        "Track and manage your carbon footprint. Seamlessly map your energy consumption against precise emission factors to monitor greenhouse gas metrics, simplify compliance, and accelerate your path to net-zero.",
      ),
      highlights: [
        gettext("Track Carbon Footprint"),
        gettext("Monitor Emissions"),
        gettext("Simplify Compliance"),
      ],
    },
    {
      name: gettext("Cost"),
      price: 119,
      description: gettext(
        "Drive financial efficiency. Identify operational waste, isolate expensive usage spikes, and deploy cost-compression strategies to actively shrink your energy bills and maximize your budget.",
      ),
      highlights: [
        gettext("Compress Energy Spend"),
        gettext("Eliminate Operational Waste"),
        gettext("Maximize Your Budget"),
      ],
    },
    {
      name: gettext("Contracts"),
      price: 179,
      description: gettext(
        "Benchmark your rates. Input your current energy contract details to compare your actual provider rates against live market data, helping you spot better deals and ensure you are never overpaying.",
      ),
      highlights: [
        gettext("Benchmark Provider Rates"),
        gettext("Compare Market Data"),
        gettext("Spot Better Deals"),
      ],
    },
  ];

  const reportOfferings: ProductOffering[] = [
    {
      name: gettext("Energy"),
      price: 59,
      description: gettext(
        "Download comprehensive, pre-formatted Excel sheets detailing your full consumption history. Instantly analyze your baseline usage, review demand trends, and share clean data with your team for auditing and planning.",
      ),
      highlights: [
        gettext("Download Excel History"),
        gettext("Analyze Usage Trends"),
        gettext("Export Baseline Data"),
      ],
    },
    {
      name: gettext("Climate"),
      price: 59,
      description: gettext(
        "Download detailed Excel summaries of your environmental impact. Easily track your carbon footprint, review emission trends over time, and export the exact greenhouse gas metrics you need for corporate sustainability and compliance reporting.",
      ),
      highlights: [
        gettext("Export Carbon Metrics"),
        gettext("Download Emission History"),
        gettext("Simplify ESG Reporting"),
      ],
    },
    {
      name: gettext("Tariff"),
      price: 79,
      description: gettext(
        "Download comprehensive Excel breakdowns of your energy pricing structures and rate plans. Easily analyze utility tariff charges, isolate peak versus off-peak costs, and identify the most cost-effective pricing tiers for your operations.",
      ),
      highlights: [
        gettext("Download Rate Breakdowns"),
        gettext("Analyze Pricing Tiers"),
        gettext("Isolate Peak Costs"),
      ],
    },
  ];

  const apiOfferings: ProductOffering[] = [
    {
      name: gettext("Energy"),
      price: 249,
      description: gettext(
        "Stream live consumption data directly into your existing systems. Automate real-time tracking of your power usage, capture immediate usage spikes, and feed your internal dashboards with precise, programmatic energy metrics.",
      ),
      highlights: [
        gettext("Stream Live Consumption"),
        gettext("Automate Usage Tracking"),
        gettext("Capture Immediate Spikes"),
      ],
    },
    {
      name: gettext("Climate"),
      price: 229,
      description: gettext(
        "Pull real-time carbon emissions and environmental footprint data straight into your software. Automate your sustainability tracking, feed greenhouse gas metrics directly into your compliance engines, and programmatically monitor your progress toward net-zero targets.",
      ),
      highlights: [
        gettext("Stream Live Emissions"),
        gettext("Automate Sustainability Tracking"),
        gettext("Sync Carbon Metrics"),
      ],
    },
    {
      name: gettext("Cost"),
      price: 219,
      description: gettext(
        "Integrate real-time financial tracking directly into your enterprise software. Programmatically pull current energy expenditures, monitor cost-compression metrics, and feed live utility pricing data straight into your accounting or ERP systems for automated budget management.",
      ),
      highlights: [
        gettext("Stream Live Expenditures"),
        gettext("Feed Accounting Systems"),
        gettext("Automate Budget Tracking"),
      ],
    },
  ];

  const sections = [
    {
      title: gettext("Services"),
      subtitle: gettext(
        "We equip your business with the data, tools, and flexibility needed to optimize every aspect of your energy ecosystem.",
      ),
      icon: <MdOutlineTrendingUp size={22} />,
      chip: gettext("Web access"),
      color: "#123B66",
      offerings: serviceOfferings,
      badge: gettext("4 offerings"),
    },
    {
      title: gettext("Reports"),
      subtitle: gettext(
        "Get the exact data you need, precisely when and how you need it. Our reporting tools turn complex energy metrics into clear, actionable insights for your team or stakeholders.",
      ),
      icon: <MdOutlineDescription size={22} />,
      chip: gettext("Excel export"),
      color: "#5A3A18",
      offerings: reportOfferings,
      badge: gettext("3 reports"),
    },
    {
      title: gettext("API access"),
      subtitle: gettext(
        "Integrate our energy intelligence directly into your existing infrastructure. Built for developers and data teams, our robust API lets you automate workflows and power your own internal applications with real-time data.",
      ),
      icon: <MdOutlineApi size={22} />,
      chip: gettext("API integration"),
      color: "#144C39",
      offerings: apiOfferings,
      badge: gettext("3 plans"),
    },
  ];

  const stats = [
    {
      label: gettext("Total offerings"),
      value: "10",
      icon: <MdOutlineSettingsSuggest size={20} />,
    },
    {
      label: gettext("Web services"),
      value: "4",
      icon: <MdOutlineCloud size={20} />,
    },
    {
      label: gettext("Downloadable reports"),
      value: "3",
      icon: <MdOutlineAssessment size={20} />,
    },
    {
      label: gettext("API access plans"),
      value: "3",
      icon: <MdOutlineSensors size={20} />,
    },
  ];

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
          mb: 3,
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.main,
          // background:
          //   "linear-gradient(135deg, #11253F 0%, #1D4E7A 48%, #C98B32 100%)",
          boxShadow: "0 24px 60px rgba(17, 37, 63, 0.22)",
          // "&:before": {
          //   content: '""',
          //   position: "absolute",
          //   inset: 0,
          //   background:
          //     "radial-gradient(circle at top right, rgba(255,255,255,0.26), transparent 36%), radial-gradient(circle at bottom left, rgba(255,255,255,0.12), transparent 30%)",
          //   pointerEvents: "none",
          // },
        })}
      >
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={gettext("Smart Analytics")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
            <Chip
              label={gettext("Cost Compression")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
            <Chip
              label={gettext("Seamless Contracting")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
            <Chip
              label={gettext("Data Your Way")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
          </Box>

          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {gettext("Products")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ mt: 1, maxWidth: 780, color: alpha("#FFFFFF", 0.9) }}
            >
              {gettext(
                "Streamline your energy management, slash costs, and hit your sustainability goals with our all-in-one platform. We turn complex data into actionable power.",
              )}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {stats.map((stat) => (
              <Grid key={stat.label} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={(theme) => ({
                    height: "100%",
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.common.white, 0.18),
                    boxShadow: "none",
                    backdropFilter: "blur(6px)",
                  })}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha("#FFFFFF", 0.14),
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 800, color: "common.white" }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: alpha("#FFFFFF", 0.88) }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>

      <Stack spacing={3}>
        {sections.map((section) => (
          <Card
            key={section.title}
            sx={(theme) => ({
              borderRadius: 4,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.2),
              boxShadow: "0 14px 42px rgba(15, 23, 42, 0.06)",
              overflow: "hidden",
            })}
          >
            <Box
              sx={(theme) => ({
                px: { xs: 2.5, md: 3 },
                py: { xs: 2.5, md: 3 },
                backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                borderBottom: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.2),
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      color: section.color,
                      bgcolor: alpha(section.color, 0.08),
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {section.subtitle}
                    </Typography>
                  </Box>
                </Box>

                <Chip
                  label={section.badge}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Grid container spacing={2.5}>
                {section.offerings.map((offering) => (
                  <Grid key={offering.name} size={{ xs: 12, md: 6, lg: 4 }}>
                    <Card
                      variant="outlined"
                      sx={(theme) => ({
                        height: "100%",
                        borderRadius: 3,
                        backgroundColor: alpha(
                          theme.palette.background.default,
                          0.5,
                        ),
                        transition:
                          "transform 160ms ease, box-shadow 160ms ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
                        },
                      })}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Stack spacing={2} sx={{ height: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 1.25,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={(theme) => ({
                                  width: 42,
                                  height: 42,
                                  borderRadius: 2,
                                  display: "grid",
                                  placeItems: "center",
                                  color: section.color,
                                  bgcolor: alpha(
                                    theme.palette.common.black,
                                    0.04,
                                  ),
                                })}
                              >
                                <MdOutlineEuro size={20} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 800 }}
                                >
                                  {offering.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {section.title}
                                </Typography>
                              </Box>
                            </Box>

                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                                color: section.color,
                              }}
                            >
                              {formatPrice(offering.price)}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {offering.description}
                          </Typography>

                          <Divider />

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {offering.highlights.map((highlight) => (
                              <Chip
                                key={highlight}
                                label={highlight}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            ))}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};
